import { useEffect, useRef } from 'react';
import {
  Button,
  StackLayout,
  HeadingText,
  Modal,
  ParagraphText,
  ImageMessage,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { logout } from 'common/logout';
import { ErrorState } from '@leagueplatform/web-common-components';
import { BaseModalContainer } from 'components/modals/common/base-modal.component';
import { useDeleteAccount } from 'hooks/mutations/use-delete-account.hook';
import { useGetCaregiverStatus } from 'hooks/use-get-caregiver-status.hook';
import { useGetUserProfile } from 'hooks/use-get-user-profile.hook';
import { LoadingSpinner } from 'components/loading-spinner/loading-spinner.component';
import { QUERY_PARAM_KEYS, QUERY_PARAM_MODAL_VALUE } from 'common/constants';
import deleteAccountIcon from 'assets/delete-account-icon.svg';

export const DeleteAccountModal = ({ onClose }: { onClose: () => void }) => {
  const { formatMessage } = useIntl();
  const { isImpersonationStarted } = useGetCaregiverStatus();
  const {
    mutate,
    isLoading: isAccountDeleteLoading,
    isError,
    error: mutationError,
    reset,
  } = useDeleteAccount();
  const {
    data,
    isSuccess,
    isLoading,
    error: profileError,
    refetch,
  } = useGetUserProfile();

  const prevIsSuccessRef = useRef(isSuccess);
  const prevIsErrorRef = useRef(isError);
  const successContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!prevIsSuccessRef.current && isSuccess) {
      successContentRef.current?.focus();
    }
    prevIsSuccessRef.current = isSuccess;
  }, [isSuccess]);

  useEffect(() => {
    if (prevIsErrorRef.current && !isError && isSuccess) {
      successContentRef.current?.focus();
    }
    prevIsErrorRef.current = isError;
  }, [isError, isSuccess]);

  if (isImpersonationStarted) return null;

  if (isLoading) {
    return (
      <BaseModalContainer
        title={formatMessage({ id: 'STR_DELETE_YOUR_ACCOUNT' })}
      >
        <LoadingSpinner />
      </BaseModalContainer>
    );
  }

  if (!isSuccess) {
    return (
      <BaseModalContainer
        title={formatMessage({ id: 'STR_DELETE_YOUR_ACCOUNT' })}
      >
        <div role="alert">
          <ErrorState error={profileError} onButtonClick={() => refetch()} />
        </div>
      </BaseModalContainer>
    );
  }

  if (isError) {
    return (
      <BaseModalContainer
        title={formatMessage({ id: 'STR_DELETE_YOUR_ACCOUNT' })}
      >
        <div role="alert">
          <ErrorState error={mutationError} onButtonClick={reset} />
        </div>
      </BaseModalContainer>
    );
  }

  const { user_id: userId } = data;

  const handleDeleteAccount = () => {
    mutate(userId, {
      onSuccess: () => {
        const queryParam = new URLSearchParams();
        queryParam.set(
          QUERY_PARAM_KEYS.MODAL,
          QUERY_PARAM_MODAL_VALUE.DELETE_ACCOUNT_DISCLAIMER,
        );
        onClose();
        logout(`${window.location.origin}?${queryParam.toString()}`);
      },
    });
  };

  const appName = formatMessage({ id: 'STR_APP_NAME' });

  return (
    <BaseModalContainer
      closeDisabled={isAccountDeleteLoading}
      footer={
        <StackLayout
          horizontalAlignment={{ '@initial': 'end', '@mobile': 'center' }}
          orientation={{ '@initial': 'horizontal', '@mobile': 'vertical' }}
          spacing="$one"
          css={{ flexGrow: 0 }}
        >
          <Modal.Close asChild>
            <Button
              priority="secondary"
              size="large"
              width={{ '@initial': 'hugContents', '@mobile': 'fillContainer' }}
              disabled={isAccountDeleteLoading}
              css={{ '@mobile': { order: 2 } }}
            >
              {formatMessage({ id: 'STR_NEVER_MIND' })}
            </Button>
          </Modal.Close>
          <Button
            data-testid="confirm-delete-btn"
            priority="primary"
            size="large"
            width={{ '@initial': 'hugContents', '@mobile': 'fillContainer' }}
            destructive
            loading={isAccountDeleteLoading}
            disabled={isAccountDeleteLoading}
            onClick={handleDeleteAccount}
            css={{ '@mobile': { order: 1 } }}
          >
            {formatMessage({ id: 'STR_YES_DELETE_ACCOUNT' })}
          </Button>
        </StackLayout>
      }
    >
      <div ref={successContentRef} tabIndex={-1}>
        <ImageMessage
          image={deleteAccountIcon}
          imageGap={{ '@initial': '$oneAndHalf', '@mobile': '$one' }}
          css={{
            padding: '$oneAndHalf $none $three',
            textAlign: 'left',
            '@mobile': { padding: '$oneAndHalf $none $twoAndHalf' },
          }}
        >
          <Modal.Title asChild>
            <HeadingText
              level="2"
              size="lg"
              css={{ paddingBlock: '$half', textAlign: 'left' }}
            >
              {formatMessage({ id: 'STR_ARE_YOU_SURE' })}
            </HeadingText>
          </Modal.Title>
          <ParagraphText css={{ marginBlockEnd: '$half !important' }}>
            {formatMessage({ id: 'STR_DELETE_ACCOUNT_BODY_1' }, { appName })}
          </ParagraphText>
          <ParagraphText>
            {formatMessage({ id: 'STR_DELETE_ACCOUNT_BODY_2' }, { appName })}
          </ParagraphText>
        </ImageMessage>
      </div>
    </BaseModalContainer>
  );
};
